/**
 * ChatPage — Landlord chat with file/image sharing.
 *
 * Left panel: one conversation per active tenant_link.
 * Right panel: messages + attachments, real-time via Supabase Realtime.
 *
 * Table: chat_messages (avoids collision with Supabase internal realtime.messages).
 */

import { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, Loader2, Paperclip, FileText, X, Download, ArrowLeft } from 'lucide-react';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { createNotification } from '../utils/notifications';
import { usePresenceFor } from '../lib/presence';
import PresenceIndicator from '../components/PresenceIndicator';

interface Conversation {
    tenantLinkId: string;
    propertyId: string;
    propertyName: string;
    tenantName: string;
    tenantEmail: string;
    tenantUserId: string | null;
    unread: number;
}

interface ChatMsg {
    id: string;
    tenant_link_id: string;
    property_id: string;
    sender_id: string;
    sender_role: 'landlord' | 'tenant';
    content: string;
    attachment_url: string | null;
    attachment_name: string | null;
    read_at: string | null;
    created_at: string;
}

export default function ChatPage() {
    const { user } = useAppContext();
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [loadingConvs, setLoadingConvs] = useState(true);
    const [loadingMsgs, setLoadingMsgs] = useState(false);

    // Live presence for every tenant in the sidebar
    const presence = usePresenceFor(conversations.map(c => c.tenantUserId));

    // Attachment state
    const [attachFile, setAttachFile] = useState<File | null>(null);
    const [attachPreview, setAttachPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const bottomRef = useRef<HTMLDivElement>(null);
    const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    // ── Load conversations ───────────────────────────────────────────────────
    useEffect(() => {
        if (!user.id) return;
        (async () => {
            setLoadingConvs(true);
            const { data: props } = await supabase
                .from('properties')
                .select('id, name')
                .eq('owner_id', user.id);

            if (!props?.length) { setLoadingConvs(false); return; }

            const propertyIds = props.map(p => p.id);
            const { data: links } = await supabase
                .from('tenant_links')
                .select('id, property_id, tenant_user_id, tenant_email, tenant_name')
                .in('property_id', propertyIds)
                .eq('invite_status', 'active');

            if (!links?.length) { setLoadingConvs(false); return; }

            const convs: Conversation[] = await Promise.all(
                links.map(async (link) => {
                    const prop = props.find(p => p.id === link.property_id);
                    const { count } = await supabase
                        .from('chat_messages')
                        .select('*', { count: 'exact', head: true })
                        .eq('tenant_link_id', link.id)
                        .eq('sender_role', 'tenant')
                        .is('read_at', null);

                    return {
                        tenantLinkId: link.id,
                        propertyId: link.property_id,
                        propertyName: prop?.name ?? 'Property',
                        tenantName: link.tenant_name || link.tenant_email,
                        tenantEmail: link.tenant_email,
                        tenantUserId: link.tenant_user_id,
                        unread: count ?? 0,
                    };
                })
            );

            setConversations(convs);
            setLoadingConvs(false);
        })();
    }, [user.id]);

    // ── Load messages + realtime ─────────────────────────────────────────────
    useEffect(() => {
        if (!selectedConv) return;

        if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
        }

        setLoadingMsgs(true);
        setMessages([]);

        supabase
            .from('chat_messages')
            .select('*')
            .eq('tenant_link_id', selectedConv.tenantLinkId)
            .order('created_at', { ascending: true })
            .then(({ data, error }) => {
                if (error) console.error('Load messages error:', error);
                setMessages(data ?? []);
                setLoadingMsgs(false);

                // Mark tenant messages as read
                supabase
                    .from('chat_messages')
                    .update({ read_at: new Date().toISOString() })
                    .eq('tenant_link_id', selectedConv.tenantLinkId)
                    .eq('sender_role', 'tenant')
                    .is('read_at', null)
                    .then(() => {
                        setConversations(prev =>
                            prev.map(c => c.tenantLinkId === selectedConv.tenantLinkId ? { ...c, unread: 0 } : c)
                        );
                    });
            });

        const channel = supabase
            .channel(`chat:landlord:${selectedConv.tenantLinkId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `tenant_link_id=eq.${selectedConv.tenantLinkId}` },
                (payload) => {
                    const msg = payload.new as ChatMsg;
                    setMessages(prev => [...prev, msg]);
                    if (msg.sender_role === 'tenant') {
                        supabase.from('chat_messages').update({ read_at: new Date().toISOString() }).eq('id', msg.id);
                    }
                }
            )
            .subscribe();

        channelRef.current = channel;
        return () => { supabase.removeChannel(channel); };
    }, [selectedConv?.tenantLinkId]);

    // ── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── File picker ──────────────────────────────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAttachFile(file);
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setAttachPreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setAttachPreview(null);
        }
        e.target.value = '';
    };

    const clearAttach = () => { setAttachFile(null); setAttachPreview(null); };

    // ── Send message ─────────────────────────────────────────────────────────
    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!draft.trim() && !attachFile) || !selectedConv || !user.id) return;
        setSending(true);
        const content = draft.trim();
        setDraft('');

        let attachment_url: string | null = null;
        let attachment_name: string | null = null;

        // Upload attachment if any
        if (attachFile) {
            const ext = attachFile.name.split('.').pop() ?? 'bin';
            const path = `${selectedConv.tenantLinkId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error: upErr } = await supabase.storage
                .from('chat-attachments')
                .upload(path, attachFile, { upsert: false });

            if (upErr) {
                console.error('Upload error:', upErr);
            } else {
                const { data: urlData } = supabase.storage.from('chat-attachments').getPublicUrl(path);
                attachment_url = urlData.publicUrl;
                attachment_name = attachFile.name;
            }
            clearAttach();
        }

        const { error } = await supabase.from('chat_messages').insert({
            tenant_link_id: selectedConv.tenantLinkId,
            property_id: selectedConv.propertyId,
            sender_id: user.id,
            sender_role: 'landlord',
            content: content || (attachment_name ? `Sent ${attachment_name}` : ''),
            attachment_url,
            attachment_name,
        });

        if (error) {
            console.error('Send error:', error);
            setDraft(content);
        } else if (selectedConv.tenantUserId) {
            await createNotification(
                selectedConv.tenantUserId,
                'new_message',
                'New message from your landlord',
                (content || attachment_name || '').slice(0, 80),
                '/app/tenant/chat'
            );
        }
        setSending(false);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const formatTime = (iso: string) =>
        new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const formatDay = (iso: string) => {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Yesterday';
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const isImage = (name: string | null) =>
        !!name && /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name);

    const grouped: { date: string; msgs: ChatMsg[] }[] = [];
    for (const m of messages) {
        const day = formatDay(m.created_at);
        const last = grouped[grouped.length - 1];
        if (last?.date === day) last.msgs.push(m);
        else grouped.push({ date: day, msgs: [m] });
    }

    // ── Render ───────────────────────────────────────────────────────────────
    if (loadingConvs) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="page" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <MessageSquare size={40} color="var(--color-text-tertiary)" style={{ margin: '0 auto 1.5rem', display: 'block' }} />
                <h2 style={{ margin: '0 0 0.75rem' }}>No active tenants yet</h2>
                <p style={{ color: 'var(--color-text-secondary)', maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>
                    Once tenants are linked to your properties they'll appear here.
                </p>
            </div>
        );
    }

    const showChat = selectedConv !== null;

    return (
        <div className="page chat-page">
            {/* ── Left panel ── */}
            <div className={`chat-page__sidebar${showChat ? ' chat-page__sidebar--hidden' : ''}`}>
                <div style={{ padding: '1.5rem 1.25rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.02em' }}>Messages</h2>
                    <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 500 }}>
                        {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {conversations.map(conv => {
                        const sel = selectedConv?.tenantLinkId === conv.tenantLinkId;
                        return (
                            <button
                                key={conv.tenantLinkId}
                                onClick={() => setSelectedConv(conv)}
                                style={{
                                    width: '100%', padding: '1rem 1.25rem', background: sel ? '#fff' : 'transparent',
                                    border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left',
                                    borderLeft: sel ? '3px solid #16a34a' : '3px solid transparent', transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px', gap: 8 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                        {conv.tenantUserId && (
                                            <PresenceIndicator lastSeen={presence.get(conv.tenantUserId)} size={9} />
                                        )}
                                        <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#1a1d2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                            {conv.tenantName}
                                        </span>
                                    </span>
                                    {conv.unread > 0 && (
                                        <span style={{ background: '#16a34a', color: '#fff', borderRadius: 999, fontSize: '0.7rem', fontWeight: 800, padding: '2px 7px', minWidth: 20, textAlign: 'center', flexShrink: 0, marginLeft: 8 }}>
                                            {conv.unread}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {conv.propertyName}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className={`chat-page__main${showChat ? ' chat-page__main--visible' : ''}`}>
                {!selectedConv ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: '0.75rem' }}>
                        <MessageSquare size={36} />
                        <p style={{ margin: 0, fontWeight: 500, fontSize: '1rem' }}>Select a conversation to start chatting</p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                className="chat-page__back-btn"
                                onClick={() => setSelectedConv(null)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontWeight: 800, color: '#1a1d2e', fontSize: '1.0625rem' }}>{selectedConv.tenantName}</span>
                                    {selectedConv.tenantUserId && (
                                        <PresenceIndicator
                                            lastSeen={presence.get(selectedConv.tenantUserId)}
                                            variant="full"
                                        />
                                    )}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>{selectedConv.propertyName}</div>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="chat-page__messages">
                            {loadingMsgs ? (
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Loader2 className="animate-spin" size={28} color="#94a3b8" />
                                </div>
                            ) : messages.length === 0 ? (
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.9375rem', fontWeight: 500 }}>
                                    No messages yet — say hello!
                                </div>
                            ) : (
                                grouped.map(group => (
                                    <div key={group.date}>
                                        <div style={{ textAlign: 'center', margin: '1rem 0 0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#cbd5e1', letterSpacing: '0.04em' }}>
                                            {group.date}
                                        </div>
                                        {group.msgs.map(m => {
                                            const isMe = m.sender_role === 'landlord';
                                            return (
                                                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                                                    <div className="chat-bubble" style={{
                                                        maxWidth: '68%', padding: '0.625rem 1rem',
                                                        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                        background: isMe ? '#16a34a' : '#f1f5f9',
                                                        color: isMe ? '#fff' : '#1a1d2e',
                                                        fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5, wordBreak: 'break-word',
                                                    }}>
                                                        {m.attachment_url && (
                                                            isImage(m.attachment_name) ? (
                                                                <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: m.content ? '6px' : 0 }}>
                                                                    <img
                                                                        src={m.attachment_url}
                                                                        alt={m.attachment_name ?? 'image'}
                                                                        style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, display: 'block' }}
                                                                    />
                                                                </a>
                                                            ) : (
                                                                <a
                                                                    href={m.attachment_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                                        padding: '8px 12px', borderRadius: 10, marginBottom: m.content ? '6px' : 0,
                                                                        background: isMe ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                                                                        textDecoration: 'none', color: 'inherit', fontSize: '0.8125rem', fontWeight: 600,
                                                                    }}
                                                                >
                                                                    <FileText size={16} style={{ flexShrink: 0 }} />
                                                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {m.attachment_name ?? 'File'}
                                                                    </span>
                                                                    <Download size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                                </a>
                                                            )
                                                        )}
                                                        {m.content && !m.content.startsWith('Sent ') && m.content}
                                                        {m.content && m.content.startsWith('Sent ') && !m.attachment_url && m.content}
                                                        <div style={{ fontSize: '0.7rem', marginTop: '3px', opacity: 0.6, textAlign: 'right' }}>
                                                            {formatTime(m.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                            <div ref={bottomRef} style={{ height: 8 }} />
                        </div>

                        {/* Attachment preview bar */}
                        {attachFile && (
                            <div className="chat-page__attach-preview">
                                {attachPreview ? (
                                    <img src={attachPreview} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                                ) : (
                                    <div style={{ width: 48, height: 48, borderRadius: 8, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FileText size={20} color="#64748b" />
                                    </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1a1d2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {attachFile.name}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                        {(attachFile.size / 1024).toFixed(0)} KB
                                    </div>
                                </div>
                                <button
                                    onClick={clearAttach}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        )}

                        {/* Input bar */}
                        <form onSubmit={handleSend} className="chat-page__input-bar">
                            <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv" style={{ display: 'none' }} onChange={handleFileChange} />

                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                title="Attach file or image"
                                style={{ padding: '0.625rem', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'flex', flexShrink: 0 }}
                            >
                                <Paperclip size={18} />
                            </button>

                            <input
                                type="text"
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                placeholder={`Message ${selectedConv.tenantName}…`}
                                style={{ flex: 1, padding: '0.75rem 1.125rem', borderRadius: '14px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '0.9375rem', fontWeight: 500, color: '#1a1d2e', outline: 'none' }}
                                onFocus={e => (e.target.style.borderColor = '#16a34a')}
                                onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                            />

                            <button
                                type="submit"
                                disabled={(!draft.trim() && !attachFile) || sending}
                                style={{
                                    padding: '0.75rem 1.25rem', borderRadius: '14px', border: 'none', background: '#16a34a',
                                    color: '#fff', cursor: (draft.trim() || attachFile) ? 'pointer' : 'default',
                                    display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '0.9375rem',
                                    opacity: (!draft.trim() && !attachFile) ? 0.45 : 1, transition: 'opacity 0.15s', flexShrink: 0,
                                }}
                            >
                                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
